import React from "react";
import { 
  FiXCircle, 
  FiAlertTriangle, 
  FiHome,
  FiRotateCcw,
  FiCreditCard,
  FiHelpCircle,
  FiMail,
  FiMessageSquare
} from "react-icons/fi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../layout/Navbar";
import Footer from "../../layout/Footer";

const FailedPayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  // Extract payment details from URL
  const paymentDetails = {
    amount: queryParams.get("amt") || "0.00",
    orderId: queryParams.get("ref") || queryParams.get("orderId") || "N/A",
    email: queryParams.get("email") || "N/A",
    name: queryParams.get("name") || "Student",
    examType: queryParams.get("examType") || "exam",
    reason: queryParams.get("reason") || "Payment processing failed",
    status: queryParams.get("status") || "failed"
  };

  const handleRetryPayment = () => {
    // Get cart from localStorage and redirect back to the exam type page
    const examType = paymentDetails.examType.toLowerCase();
    const savedCart = localStorage.getItem(`pending_cart_${examType}`);
    
    if (savedCart) {
      // Restore the cart
      localStorage.setItem(`cart_${examType}`, savedCart);
      
      // Navigate back to the exam type page
      navigate(`/questions/${examType}`);
    } else {
      // If no cart saved, go to home
      navigate("/questions");
    }
  };

  const handleContactSupport = () => {
    const subject = `Payment Failed - Order: ${paymentDetails.orderId}`;
    const body = `Hello Support Team,\n\nMy payment for past questions failed with the following details:\n\nOrder ID: ${paymentDetails.orderId}\nAmount: GHS ${paymentDetails.amount}\nEmail: ${paymentDetails.email}\nExam Type: ${paymentDetails.examType}\n\nPlease assist with resolving this issue.\n\nThank you.`;
    
    window.location.href = `mailto:support@pastq.eddcodesgh.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  // Common payment failure reasons and solutions
  const failureReasons = [
    {
      reason: "Insufficient funds",
      solution: "Check your account balance and try again"
    },
    {
      reason: "Expired card",
      solution: "Update your card details or use a different card"
    },
    {
      reason: "Network error",
      solution: "Check your internet connection and retry"
    },
    {
      reason: "Transaction declined",
      solution: "Contact your bank for authorization"
    },
    {
      reason: "Incorrect details",
      solution: "Verify your payment information"
    }
  ];

  return (
    <>
      <Navbar />
      
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-8 col-xl-6">
            <div className="card border-0 shadow-lg overflow-hidden">
              {/* Error Header */}
              <div className="bg-danger text-white p-4 text-center">
                <div className="d-flex justify-content-center align-items-center mb-3">
                  <FiXCircle size={40} className="me-3" />
                  <h1 className="h3 mb-0">Payment Unsuccessful</h1>
                </div>
                <p className="mb-0 opacity-90">
                  We couldn't process your payment for the past questions
                </p>
              </div>

              <div className="card-body p-4 p-lg-5">
                {/* Main Message */}
                <div className="text-center mb-5">
                  <div className="display-1 text-danger mb-3">
                    <FiXCircle />
                  </div>
                  <h2 className="fw-bold mb-3">Payment Failed</h2>
                  <p className="lead text-muted mb-0">
                    Your attempt to purchase past questions was not successful. 
                    No funds have been deducted from your account.
                  </p>
                </div>

                {/* Payment Details Card */}
                <div className="card border-light shadow-sm mb-4">
                  <div className="card-header bg-light">
                    <h5 className="mb-0 fw-bold">
                      <FiCreditCard className="me-2" />
                      Transaction Details
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <small className="text-muted d-block">Order Reference</small>
                        <code className="fw-bold text-dark">{paymentDetails.orderId}</code>
                      </div>
                      <div className="col-md-6 mb-3">
                        <small className="text-muted d-block">Amount</small>
                        <div className="fw-bold fs-4 text-danger">GHS {paymentDetails.amount}</div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <small className="text-muted d-block">Exam Type</small>
                        <span className="badge bg-primary">{paymentDetails.examType.toUpperCase()}</span>
                      </div>
                      <div className="col-md-6 mb-3">
                        <small className="text-muted d-block">Email</small>
                        <div className="fw-bold text-truncate">{paymentDetails.email}</div>
                      </div>
                    </div>
                    
                    {paymentDetails.reason && paymentDetails.reason !== "Payment processing failed" && (
                      <div className="alert alert-warning mt-3">
                        <FiAlertTriangle className="me-2" />
                        <strong>Reason:</strong> {paymentDetails.reason}
                      </div>
                    )}
                  </div>
                </div>

                {/* Common Issues & Solutions */}
                <div className="card border-light shadow-sm mb-4">
                  <div className="card-header bg-light">
                    <h5 className="mb-0 fw-bold">
                      <FiHelpCircle className="me-2" />
                      Common Issues & Solutions
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      {failureReasons.map((item, index) => (
                        <div key={index} className="col-md-6 mb-3">
                          <div className="d-flex">
                            <div className="flex-shrink-0">
                              <div className="bg-danger bg-opacity-10 text-danger rounded-circle d-flex align-items-center justify-content-center"
                                   style={{ width: '36px', height: '36px' }}>
                                {index + 1}
                              </div>
                            </div>
                            <div className="flex-grow-1 ms-3">
                              <h6 className="fw-bold mb-1">{item.reason}</h6>
                              <p className="text-muted small mb-0">{item.solution}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="d-grid gap-3 mb-4">
                  <button
                    onClick={handleRetryPayment}
                    className="btn btn-danger btn-lg py-3"
                  >
                    <FiRotateCcw className="me-2" />
                    Try Payment Again
                  </button>
                  
                  <Link to="/questions" className="btn btn-outline-secondary btn-lg py-3">
                    <FiHome className="me-2" />
                    Browse More Questions
                  </Link>
                </div>

                {/* Contact Support Section */}
                <div className="text-center">
                  <div className="d-flex flex-column flex-md-row justify-content-center align-items-center gap-3 mb-3">
                    <button
                      onClick={handleContactSupport}
                      className="btn btn-outline-primary"
                    >
                      <FiMail className="me-2" />
                      Email Support
                    </button>
                    
                    <a 
                      href="https://wa.me/233XXXXXXXXX" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-success"
                    >
                      <FiMessageSquare className="me-2" />
                      WhatsApp Support
                    </a>
                  </div>
                  
                  <p className="text-muted small">
                    Need immediate assistance? Contact our support team
                    <br />
                    <strong>Email:</strong> support@pastq.eddcodesgh.com | 
                    <strong> Phone:</strong> +233 20 123 4567
                  </p>
                </div>

                {/* Important Notes */}
                <div className="alert alert-info mt-4">
                  <div className="d-flex">
                    <FiAlertTriangle className="flex-shrink-0 me-3" size={24} />
                    <div>
                      <h6 className="alert-heading fw-bold">Important Information</h6>
                      <ul className="mb-0 small">
                        <li>Your payment was not processed successfully</li>
                        <li>No past questions have been added to your account</li>
                        <li>You can safely retry the payment</li>
                        <li>If funds were deducted, they will be refunded within 24 hours</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="mt-4">
              <div className="accordion" id="paymentFAQ">
                <div className="accordion-item border-0 shadow-sm">
                  <h2 className="accordion-header">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq1">
                      What happens if money was deducted but payment failed?
                    </button>
                  </h2>
                  <div id="faq1" className="accordion-collapse collapse" data-bs-parent="#paymentFAQ">
                    <div className="accordion-body">
                      If funds were deducted from your account but the payment failed, 
                      the amount will be automatically refunded to your account within 
                      24 hours. Contact support if you don't see the refund after 48 hours.
                    </div>
                  </div>
                </div>
                
                <div className="accordion-item border-0 shadow-sm mt-2">
                  <h2 className="accordion-header">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq2">
                      Can I use a different payment method?
                    </button>
                  </h2>
                  <div id="faq2" className="accordion-collapse collapse" data-bs-parent="#paymentFAQ">
                    <div className="accordion-body">
                      Yes, you can try with a different payment method. We accept 
                      Mobile Money (MTN, Vodafone, AirtelTigo) and all major credit/debit cards.
                    </div>
                  </div>
                </div>
                
                <div className="accordion-item border-0 shadow-sm mt-2">
                  <h2 className="accordion-header">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq3">
                      Will my cart items be saved?
                    </button>
                  </h2>
                  <div id="faq3" className="accordion-collapse collapse" data-bs-parent="#paymentFAQ">
                    <div className="accordion-body">
                      Your selected items are saved in your cart. When you click 
                      "Try Payment Again", your cart will be restored so you can 
                      complete your purchase.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      <style jsx>{`
        .accordion-button:not(.collapsed) {
          background-color: rgba(220, 53, 69, 0.1);
          color: #dc3545;
          font-weight: 600;
        }
        
        .accordion-button:focus {
          box-shadow: 0 0 0 0.25rem rgba(220, 53, 69, 0.25);
        }
        
        .card {
          border-radius: 15px;
        }
        
        .btn-danger {
          background: linear-gradient(135deg, #dc3545 0%, #bb2d3b 100%);
          border: none;
          transition: all 0.3s ease;
        }
        
        .btn-danger:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(220, 53, 69, 0.3);
        }
        
        .badge {
          padding: 0.5em 1em;
          font-size: 0.85em;
        }
        
        @media (max-width: 768px) {
          .card-body {
            padding: 2rem !important;
          }
          
          .display-1 {
            font-size: 4rem;
          }
          
          .btn-lg {
            padding: 0.75rem 1.5rem;
            font-size: 1rem;
          }
        }
        
        @media (max-width: 576px) {
          .card-body {
            padding: 1.5rem !important;
          }
          
          h2 {
            font-size: 1.5rem;
          }
          
          .lead {
            font-size: 1rem;
          }
        }
      `}</style>
    </>
  );
};

export default FailedPayment;